// ============================================================================
// src/listesDiffusion.js : les listes de diffusion de l'application, SOURCE
// UNIQUE côté écran (demande de Pierre du 19/10/2026 : « vérifie que la liste
// de diffusion "Bureau" apparaît bien partout où on peut inviter ou adresser
// des messages »). Règles pures, sans dépendance à l'écran.
// ----------------------------------------------------------------------------
// MIROIR de `listesConnues` (functions/lib/notesDiffusion.js), que lisent la
// diffusion des notes, le suivi des amendements, les nouveautés et les
// fondations : mêmes identifiants, mêmes noms, mêmes membres. La parité est
// tenue par src/listesDiffusionMiroir.test.js (une fixture identique donne les
// mêmes listes et les mêmes fiches des deux côtés).
//
// Les listes :
//   - les « Listes membres » (memberLists : liste d'une mutuelle, liste
//     personnalisée), genre « liste » ;
//   - les listes automatiques des instances (AG, CA, CODIR, directeurs
//     généraux, présidents), genre « instance » ;
//   - le Bureau (titulaires des postes actifs de gouvernance.postesActifs,
//     puis le DG d'Enio), genre « bureau » ;
//   - les domaines et les pôles d'Enio (pôles d'intérêt des fiches et
//     responsables de l'organigramme), genres « domaine » et « pole ».
// Sans `gouvernance` (pas encore chargée), ni Bureau ni domaines ni pôles :
// jamais une liste vide ou fausse.
//
// Seules des FICHES sortent d'une liste (`membres`, la fiche telle qu'elle
// est dans l'annuaire, donc son adresse) ; un membre d'une liste personnalisée
// sans fiche retrouvée est compté (`horsFiche`, comme le serveur) et, s'il a
// une adresse, servi à part en pseudo-identité (`horsFicheMembres`,
// « ext_<adresse> ») aux écrans qui l'acceptaient déjà (réunions, sondages,
// emailing). Le verrou src/listesDiffusionPartout.test.js fait échouer la CI
// si un écran qui propose des listes ne passe pas par ce module.
// ============================================================================
import { POSTES_BUREAU } from "./postesBureau";
import { mandatActif } from "./mandatsUtils";
import { DOMAINES_ORGANIGRAMME, polesEffectifs, responsable } from "./hestia/organigramme";
import { ficheEligible } from "./hestia/mutuelles";

/** Les listes automatiques des instances : [id, nom, rôle, type d'instance]
 *  (noms et identifiants de LISTES_AUTO, functions/lib/notesDiffusion.js). */
export const LISTES_INSTANCES = Object.freeze([
  ["__auto_ag__", "Assemblée générale (automatique)", "AG", "AG"],
  ["__auto_ca__", "Conseil d'administration (automatique)", "CA", "CA"],
  ["__auto_cd__", "CODIR (automatique)", "CD", "SE"],
  ["__auto_dg__", "Directeurs généraux (automatique)", "DG", "SE"],
  ["__auto_pr__", "Présidents (automatique)", "PR", "SE"],
]);
export const ID_LISTE_BUREAU = "__auto_bureau__";
export const NOM_LISTE_BUREAU = "Bureau (automatique)";
/** L'ordre canonique des listes automatiques (jamais un ordre de mérite) :
 *  l'ordre des instances, le Bureau après le conseil. */
export const ORDRE_AUTOMATIQUES = Object.freeze(["__auto_ag__", "__auto_ca__", ID_LISTE_BUREAU, "__auto_cd__", "__auto_dg__", "__auto_pr__"]);

/** Libellés des domaines : ceux d'ORG_DOMAINES (src/appShared.js) et de
 *  LIBELLES_DOMAINES (functions/lib/listesOrganigramme.js), parité testée. */
export const LIBELLES_DOMAINES = Object.freeze({
  dev: "Développement & Fidélisation",
  aff: "Affaires extérieures",
  imp: "Impact",
  pil: "Pilotage éco. & réglementaire",
  res: "Ressources",
});
const PREFIXE_POLE = "__auto_pole_";
const PREFIXE_DOMAINE = "__auto_domaine_";
export const idPole = (domaineId, rang) => `${PREFIXE_POLE}${domaineId}_${rang + 1}__`;
export const idDomaine = (domaineId) => `${PREFIXE_DOMAINE}${domaineId}__`;

const ID_LISTE = /^[A-Za-z0-9_-]{1,64}$/;
const estListe = (x) => (Array.isArray(x) ? x : []);
const lc = (s) => String(s || "").trim().toLocaleLowerCase("fr");
const vivant = (d) => !!d && d.actif !== false && !d.supprime;
/** Un libellé de pôle comparable (clePole du serveur). */
const clePole = (s) => String(s == null ? "" : s).normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * LE BUREAU : demande de Pierre (16/09/2026) : « la liste "Bureau" doit
 * comporter les membres du Bureau dont le mandat est en cours et le Directeur
 * général d'Enio ».
 *
 * Le Bureau ne se lit pas dans `roles` : ce sont les TITULAIRES DES POSTES
 * (gouvernance.postesActifs : président, vice-présidents, secrétaire général,
 * trésorier général, plus les postes créés à la main). C'est déjà la règle
 * d'accès aux documents du Bureau (src/accesInstances.js).
 *
 * « Mandat en cours » = le poste est ACTIF, et la personne n'est ni sortie ni
 * supprimée de l'annuaire. On ne filtre PAS sur la date de fin : doctrine
 * arrêtée par Pierre les 24-26/08/2026 (une échéance dépassée est signalée,
 * jamais appliquée toute seule, et une liste d'invitation qui se vide la
 * veille d'un renouvellement est pire que le mal).
 *
 * Le Directeur général d'Enio (rôle DG sur une fiche de la maison Enio) siège
 * au Bureau sans y tenir de poste : il ferme la liste.
 * Miroir serveur : functions/lib/bureauMembres.js (src/bureauMiroirServeur.test.js).
 */
export const membresBureau = (delegates = [], gouvernance = {}) => {
  const ordreDe = (posteId) => {
    const p = POSTES_BUREAU.find((x) => x.id === posteId);
    return p ? p.ordre : 100;  // postes personnalisés : après les statutaires
  };
  const sortiDuBureau = (d) => ["sorti", "termine"].includes(d?.mandatBureau?.statut);
  const fiches = estListe(delegates);
  const out = [];
  const vus = new Set();
  const postes = Array.isArray(gouvernance?.postesActifs) ? gouvernance.postesActifs : [];
  [...postes]
    .sort((a, b) => ordreDe(a && a.posteId) - ordreDe(b && b.posteId))
    .forEach((p) => {
      const id = p && (p.membreId || p.delegateId);
      const d = id ? fiches.find((x) => x && x.id === id) : null;
      if (!d || !vivant(d) || sortiDuBureau(d) || vus.has(d.id)) return;
      vus.add(d.id);
      out.push({ delegate: d, poste: p.posteId || "" });
    });
  // Le DG d'Enio : la maison, pas une mutuelle adhérente.
  const estEnio = (d) => /^enio$/i.test(String(d.mutuelleGouvernance || d.mutuelle || "").trim());
  fiches
    .filter((d) => vivant(d) && estEnio(d) && (d.roles || []).includes("DG") && !vus.has(d.id))
    .forEach((d) => { vus.add(d.id); out.push({ delegate: d, poste: "dg" }); });
  return out;
};

/**
 * Les membres d'une liste de « Listes membres » : les fiches dont la mutuelle
 * porte le nom de la liste, puis ses membres EXPLICITES rapprochés de
 * l'annuaire (identifiant, adresse, puis prénom, nom et mutuelle), comme le
 * serveur. Un membre sans fiche vivante est compté (`horsFiche`) ; s'il a une
 * adresse et qu'aucune fiche de l'annuaire (même inactive) ne la porte, il est
 * servi en pseudo-identité « ext_<adresse> » (`horsFicheMembres`).
 */
export function membresDeListe(l, delegates = []) {
  const toutes = estListe(delegates).filter((d) => d && d.id);
  const vivantes = toutes.filter(vivant);
  const membres = new Map();
  const horsFicheMembres = new Map();
  let horsFiche = 0;
  if (!l) return { membres: [], horsFiche, horsFicheMembres: [] };
  for (const d of vivantes) if (lc(d.mutuelle) && lc(d.mutuelle) === lc(l.name)) membres.set(d.id, d);
  for (const m of estListe(l.members)) {
    const email = lc(m && m.email);
    let d = m && m.id ? vivantes.find((x) => x.id === m.id) : null;
    if (!d && email) d = vivantes.find((x) => lc(x.email) === email);
    if (!d && m) d = vivantes.find((x) => lc(x.prenom) === lc(m.prenom) && lc(x.nom) === lc(m.nom) && lc(x.mutuelle) === lc(m.mutuelle));
    if (d) { membres.set(d.id, d); continue; }
    horsFiche += 1;
    // Une fiche sortie ou supprimée ne revient jamais par son adresse.
    const ficheEteinte = (m && m.id && toutes.some((x) => x.id === m.id)) || (email && toutes.some((x) => lc(x.email) === email));
    if (email && !ficheEteinte && !horsFicheMembres.has(email)) {
      horsFicheMembres.set(email, { id: "ext_" + email, prenom: m.prenom || "", nom: m.nom || "", mutuelle: m.mutuelle || "", email: m.email, actif: true });
    }
  }
  return { membres: [...membres.values()], horsFiche, horsFicheMembres: [...horsFicheMembres.values()] };
}

/** Les listes des domaines et des pôles (miroir de listesDomainesPoles,
 *  functions/lib/listesOrganigramme.js). */
function listesDomainesPoles(fiches, gouvernance, poleRenames) {
  const eligibles = estListe(fiches).filter(ficheEligible);
  const parId = new Map(eligibles.map((f) => [f.id, f]));
  const org = gouvernance && typeof gouvernance === "object" ? (gouvernance.orgEnio && typeof gouvernance.orgEnio === "object" ? gouvernance.orgEnio : gouvernance) : null;
  const out = [];
  for (const d of DOMAINES_ORGANIGRAMME) {
    const poles = polesEffectifs(d.id, poleRenames);
    const membresDomaine = new Map();
    poles.forEach((pole, rang) => {
      const cle = clePole(pole);
      const m = new Map(eligibles.filter((f) => Array.isArray(f.polesInteret) && f.polesInteret.some((p) => typeof p === "string" && clePole(p) === cle)).map((f) => [f.id, f]));
      const coord = org ? responsable(org, d.id, pole) : null;
      if (coord && parId.has(coord)) m.set(coord, parId.get(coord));
      for (const [id, f] of m) membresDomaine.set(id, f);
      if (m.size) out.push({ id: idPole(d.id, rang), nom: `Pôle ${pole} (automatique)`, genre: "pole", domaine: d.id, pole, membres: [...m.values()] });
    });
    const dir = org ? responsable(org, d.id) : null;
    if (dir && parId.has(dir)) membresDomaine.set(dir, parId.get(dir));
    if (membresDomaine.size) out.push({ id: idDomaine(d.id), nom: `Domaine ${LIBELLES_DOMAINES[d.id] || d.id} (automatique)`, genre: "domaine", domaine: d.id, membres: [...membresDomaine.values()] });
  }
  return out;
}

/**
 * Toutes les listes de diffusion : [{ id, nom, auto, genre, membres: [fiche],
 * ids: [identifiants triés], horsFiche, horsFicheMembres, ... }], dans l'ordre
 * alphabétique de leur nom (celui du serveur). Une liste automatique vide
 * n'est pas proposée ; une liste membres l'est toujours (comme le serveur).
 * Champs propres : `role` et `instanceType` (instances, Bureau), `domaine` et
 * `pole` (organigramme), `liste` (le document memberLists d'origine).
 *
 * @param memberLists  documents memberLists { id, name, members, couleur1… }
 * @param delegates    fiches de l'annuaire
 * @param gouvernance  gouvernance/enio (sans elle : ni Bureau, ni organigramme)
 * @param poleRenames  config/settings.poleRenames
 */
export function listesDiffusion({ memberLists = [], delegates = [], gouvernance = null, poleRenames = null } = {}) {
  const fiches = estListe(delegates).filter((d) => d && d.id);
  const vivantes = fiches.filter(vivant);
  const avecIds = (l) => ({ horsFiche: 0, horsFicheMembres: [], ...l, ids: l.membres.map((d) => d.id).sort() });
  const out = [];
  for (const l of estListe(memberLists)) {
    if (!l || !ID_LISTE.test(String(l.id || "")) || String(l.id).startsWith("__auto_")) continue;
    const r = membresDeListe(l, fiches);
    out.push(avecIds({ id: String(l.id), nom: String(l.name || l.id), auto: false, genre: "liste", liste: l, ...r }));
  }
  for (const [id, nom, role, instanceType] of LISTES_INSTANCES) {
    const membres = vivantes.filter((d) => estListe(d.roles).includes(role) && (role === "AG" ? mandatActif(d.mandatAG) : role === "CA" ? mandatActif(d.mandatCA) : true));
    if (membres.length) out.push(avecIds({ id, nom, auto: true, genre: "instance", role, instanceType, membres }));
  }
  if (gouvernance) {
    const membres = membresBureau(vivantes, gouvernance).map((m) => m.delegate);
    if (membres.length) out.push(avecIds({ id: ID_LISTE_BUREAU, nom: NOM_LISTE_BUREAU, auto: true, genre: "bureau", instanceType: "BU", membres }));
    for (const l of listesDomainesPoles(vivantes, gouvernance, poleRenames)) out.push(avecIds({ ...l, auto: true }));
  }
  return out.sort((a, b) => a.nom.localeCompare(b.nom, "fr", { sensitivity: "base" }) || a.id.localeCompare(b.id));
}

/** Les listes automatiques des instances et le Bureau, dans l'ordre
 *  canonique (AG, CA, Bureau, CODIR, DG, présidents). */
export const listesAutomatiques = (listes) =>
  ORDRE_AUTOMATIQUES.map((id) => estListe(listes).find((l) => l.id === id)).filter(Boolean);
/** Les listes membres, en ordre alphabétique. */
export const listesMembres = (listes) => estListe(listes).filter((l) => l.genre === "liste");
/** Les listes de l'organigramme, domaine par domaine (ses pôles, puis le
 *  domaine), dans l'ordre de l'organigramme. */
export function listesOrganigramme(listes) {
  const l = estListe(listes);
  return DOMAINES_ORGANIGRAMME.map((d) => ({
    domaine: d.id,
    libelle: LIBELLES_DOMAINES[d.id] || d.id,
    poles: d.poles.map((_, rang) => l.find((x) => x.id === idPole(d.id, rang))).filter(Boolean),
    liste: l.find((x) => x.id === idDomaine(d.id)) || null,
  })).filter((g) => g.poles.length || g.liste);
}
/** Les personnes d'une liste, fiches puis pseudo-identités, pour les écrans
 *  qui acceptent une adresse hors fiche (réunions, sondages, emailing). */
export const personnesDeListe = (l) => (l ? [...estListe(l.membres), ...estListe(l.horsFicheMembres)] : []);
/** Les adresses d'une liste (fiches puis hors fiche), sans doublon. */
export const adressesDeListe = (l) => [...new Set(personnesDeListe(l).map((d) => String(d.email || "").trim()).filter(Boolean))];
